import { isEqual } from "lodash";
import React, { useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { isNonEmptyArray, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";
import {
	findProjects,
	setPaging,
	setProjectSearchBar
} from "store/actions/projectList";
import { columnsSelectors, projectListSelectors } from 'store/selectors';
import { useDictionary, useFacetedSearch, usePagination } from "hooks";
import { PROJECTS_BASE_ROUTE, DEFAULT_PROPOSAL_COLUMN_HEADERS, HIDE_FROM_RELEASE } from "globals/constants";
import BasePadding from "@cayuse/shared-react.components.base-padding";
import FacetedSearchBar from "components/FacetedSearchBar";
import FeedTable from "components/FeedTable";
import PaddedCell from "@cayuse/shared-react.components.padded-cell";
import SPLink from "components/SPLink";
import VerticalSpacing from "@cayuse/shared-react.components.vertical-spacing";

import styles from "./styles.css";


const ProjectListView = ({ projectListId }) => {
	const {
		pageLimit,
		pageOffset,
		setPageLimit,
		setPageNumber
	} = usePagination("projects", setPaging);

	const { items, totalItems, search, isFetching: isLoading } = useSelector(projectListSelectors.content);
	const storedActiveColumns = useSelector(columnsSelectors.projects);
	const previousSearch = useRef({});
	const isSearchChanged = useRef(false);

	const dictionary = useDictionary("projects");

	const availableColumns = useMemo(() => ([
		{ ...DEFAULT_PROPOSAL_COLUMN_HEADERS.PROJECT_TITLE, keys: [ "title" ] },
		{ ...DEFAULT_PROPOSAL_COLUMN_HEADERS.PROJECT_NUMBER, keys: [ "projectNumber" ] },
		{ ...DEFAULT_PROPOSAL_COLUMN_HEADERS.STATUS, keys: [ "status" ] },
	]), []);

	const activeColumns = useMemo(() => {
		return isNonEmptyArray(storedActiveColumns) ? storedActiveColumns : [ ...availableColumns ];
	}, [ storedActiveColumns, availableColumns ]);

	const { searchFacets, onSearchChange } = useFacetedSearch({
		availableColumns,
		setSearchFragments: setProjectSearchBar,
		searchByKey: true
	});

	useEffect(() => {
		if (!isEqual(search, previousSearch.current)) {
			setPageNumber(1);
			previousSearch.current = search;
			isSearchChanged.current = true;
		}
	}, [ search, setPageNumber ]);

	useEffect(() => {
		if (!isLoading) {
			const offset = isSearchChanged.current ? 0 : pageOffset;
			findProjects({ pageLimit, pageOffset: offset, searchTerms: search });
			isSearchChanged.current = false;
		}
	}, [ pageLimit, pageOffset, search ]);

	const hasItems = isNonEmptyArray(items);

	const rows = hasItems
		?
		items.map(item => {
			const isProjectNumberLinkable = isNonEmptyString(item.id) && isNonEmptyString(item.projectNumber);
			const linkedProjectNumber = isProjectNumberLinkable
				? <SPLink to={`${PROJECTS_BASE_ROUTE}/${item.id}`}>{item.projectNumber}</SPLink>
				: "";

			return ({
				...item,
				projectNumber: linkedProjectNumber
			});
		})
		: [];

	return (
		<div data-component="ProjectListView">
			<VerticalSpacing>
				<BasePadding>
					{HIDE_FROM_RELEASE &&
						<PaddedCell className={styles.projectListOptions}
							radius={{ top: true }}
							border={{ top: true, left: true, right: true }}
						>
							<div className={`ui icon input ${styles.projectListSearch}`}>
								<FacetedSearchBar
									ariaLabel={dictionary.projectsSearchAriaLabel || "Search projects"}
									facetAriaControls={projectListId}
									facets={searchFacets}
									onChange={onSearchChange}
									type="PROJECT"
								/>
							</div>
						</PaddedCell>
					}
					<div>
						<FeedTable isLoading={isLoading}
								   tableid={projectListId}
								   headers={activeColumns}
								   rows={rows}
								   totalItems={totalItems}
								   pageLimit={pageLimit}
								   pageOffset={pageOffset}
								   onPageChange={setPageNumber}
								   onPageLimitChange={setPageLimit}
								   noResultsLabel={dictionary.noResults || "No Saved Projects"}
								   captionText="Projects"
						/>
					</div>
				</BasePadding>
			</VerticalSpacing>
		</div>
	);
};

ProjectListView.propTypes = {
	projectListId: PropTypes.string
};

export default ProjectListView;
